import React, { useState, useEffect, useRef } from 'react';
import rentalApi from '../services/api';
import { GTA_CITIES } from '../types';
import { AddressAutocomplete } from './AddressAutocomplete';
import './FormShared.css';

const PROP_TYPES: { label: string; icon: string }[] = [
  { label: 'Basement',      icon: '🏚' },
  { label: 'Apartment',     icon: '🏢' },
  { label: 'Condo',         icon: '🏙' },
  { label: 'Townhouse',     icon: '🏘' },
  { label: 'Semi-Detached', icon: '🏠' },
  { label: 'Detached',      icon: '🏡' },
];
const BHK_OPTS  = ['1', '1+', '2', '2+', '3+'];
const BATH_OPTS = ['1', '1+', '2', '2+', '3+'];
const AVAIL     = ['ASAP', 'This Month', 'Next Month', 'Flexible'];
const FURNISH   = ['Furnished', 'Semi-Furnished', 'Unfurnished'];
const UTILITIES = ['Hydro', 'Internet', 'Water'];
const AMENITIES = ['🏊 Pool', '🏋 Gym', '🚗 Parking', '🧖 Sauna'];
const PETS      = ['Yes', 'No', 'Not Sure'];
const TRANSIT   = ['0–5 km', '5–10 km', '10+ km'];

interface Props { onSuccess: () => void; onCancel: () => void; onCoordinatesChange?: (c: [number, number]) => void; externalCoords?: [number, number]; onOpenPrivacy?: () => void; onOpenTerms?: () => void; }

export const PostPropertyForm: React.FC<Props> = ({ onSuccess, onCancel, onCoordinatesChange, externalCoords, onOpenPrivacy, onOpenTerms }) => {
  const [address, setAddress]       = useState('');
  const [coords, setCoords]         = useState<[number, number] | null>(null);
  const [city, setCity]             = useState('Toronto');
  const [budget, setBudget]         = useState('');
  const [bhk, setBhk]               = useState<number | string | null>(null);
  const [baths, setBaths]           = useState('');
  const [propType, setPropType]     = useState('');
  const [avail, setAvail]           = useState('');
  const [furnishing, setFurnishing] = useState('');
  const [utilities, setUtilities]   = useState<string[]>([]);
  const [amenities, setAmenities]   = useState<string[]>([]);
  const [pets, setPets]             = useState('');
  const [transit, setTransit]       = useState('');
  const [schoolRating, setSchoolRating] = useState(0);
  const [description, setDescription]  = useState('');
  const [email, setEmail]           = useState('');
  const [phone, setPhone]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentAt, setConsentAt]   = useState<string | null>(null);
  const [submitted, setSubmitted]   = useState<{ link: string } | null>(null);
  const [copied, setCopied]         = useState(false);

  const coordsFromAutocompleteRef = useRef('');

  useEffect(() => {
    if (!externalCoords) return;
    const [lng, lat] = externalCoords;
    setCoords(externalCoords);
    const key = `${lng.toFixed(6)},${lat.toFixed(6)}`;
    if (coordsFromAutocompleteRef.current === key) return;
    if (!window.google?.maps) return;
    new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const r = results[0];
        setAddress(r.formatted_address);
        const comps = r.address_components ?? [];
        const cityComp = comps.find(c => c.types.includes('locality'));
        if (cityComp) setCity(cityComp.long_name);
      }
    });
  }, [externalCoords]);

  const toggleChip = (setArr: React.Dispatch<React.SetStateAction<string[]>>, val: string) =>
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const handleSubmit = async () => {
    setError('');
    if (!address) return setError('Please search and select an address');
    if (!coords) return setError('No location set — search an address or click the map');
    const [lng, lat] = coords;
    if (lat < 43.1 || lat > 44.5 || lng < -80.7 || lng > -78.4) return setError("Sorry, we don't operate in this zone yet.");
    if (city && !GTA_CITIES.includes(city)) return setError("Sorry, we don't operate in this zone yet.");
    if (!budget || parseFloat(budget) <= 0) return setError('Monthly rent is required');
    if (parseFloat(budget) > 9999) return setError('Monthly rent cannot exceed $9,999');
    if (!propType) return setError('Select a Property Type');
    if (bhk === null) return setError('Select the number of Bedrooms');
    if (!baths) return setError('Select the number of Baths');
    if (!avail) return setError('Select Availability');
    if (!email) return setError('Email is required to connect with tenants');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Enter a valid email address');
    if (!termsAccepted) return setError('Please accept the Terms of Use and Privacy Policy to continue');
    setLoading(true);
    try {
      const res = await rentalApi.createRentalListing({
        listingType: 'RentingWhole',
        location: { coordinates: coords },
        address, city: city || 'Toronto',
        budget: parseFloat(budget),
        bhk: bhk === null ? undefined : parseInt(String(bhk)),
        baths, propertyType: propType || undefined,
        availability: avail, furnishing,
        amenities: amenities.length ? amenities : undefined,
        utilitiesIncluded: utilities.length ? utilities : undefined,
        petsAllowedStatus: pets, transitDistance: transit || undefined,
        schoolRating: schoolRating || undefined,
        description: description || undefined,
        email, phone: phone || undefined, desiredTenantGender: 'Any',
        consentAcceptedAt: consentAt ?? undefined,
      });
      const { id } = res.data.data;
      const token = res.data.deleteToken;
      const link = id && token
        ? `${window.location.origin}/?listingId=${id}&deleteToken=${token}`
        : '';
      setSubmitted({ link });
    } catch (err: any) {
      setError(err.message || 'Failed to post property. Please try again.');
    } finally { setLoading(false); }
  };

  const Tgl = ({ opts, val, onSet, labelId }: { opts: (string | number)[], val: any, onSet: (v: any) => void; labelId: string }) => (
    <div className="toggle-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={String(o)} className={`tgl${val === o ? ' on' : ''}`} aria-pressed={val === o} onClick={() => onSet(o)}>{o}</button>
      ))}
    </div>
  );

  const Chips = ({ opts, sel, setSel, labelId }: { opts: string[], sel: string[], setSel: React.Dispatch<React.SetStateAction<string[]>>; labelId: string }) => (
    <div className="chip-group" role="group" aria-labelledby={labelId}>
      {opts.map(o => (
        <button type="button" key={o} className={`chip${sel.includes(o) ? ' on' : ''}`} aria-pressed={sel.includes(o)} onClick={() => toggleChip(setSel, o)}>{o}</button>
      ))}
    </div>
  );

  const sectionsComplete = [
    !!address && !!coords,
    !!budget && !!propType && bhk !== null && !!baths && !!avail,
    true,
    !!email,
  ].filter(Boolean).length;

  if (submitted) {
    const copyLink = () => {
      navigator.clipboard.writeText(submitted.link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div className="form-panel">
        <div className="form-success">
          <div className="form-success-icon">✓</div>
          <h3>Listing posted!</h3>
          <p>Save this link to remove your listing at any time.</p>
          {submitted.link && (
            <>
              <div className="form-success-link-row">
                <input className="form-success-link-input" readOnly value={submitted.link} />
                <button className={`form-success-copy${copied ? ' copied' : ''}`} onClick={copyLink}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="form-success-note">Anyone with this link can delete this listing. Keep it safe.</p>
            </>
          )}
          <button className="form-success-done" onClick={onSuccess}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-panel" role="form" aria-label="Post whole unit">
      <div className="form-panel-header">
        <div className="form-panel-eyebrow">
          <span>Post Whole Unit</span>
          <button type="button" className="form-panel-close" onClick={onCancel} aria-label="Close form"><span aria-hidden="true">✕</span></button>
        </div>
        <h2 className="form-panel-title">Rent out your whole place</h2>
        <p className="form-panel-sub">Find quality tenants for your entire unit.</p>
      </div>

      <div className="form-panel-body">
        {/* 01 Location */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">01</span>
            <span className="form-section-title">Where is it?</span>
          </div>
          <div className="fs">
            <label htmlFor="ppf-address" className="fs-label">Address <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <AddressAutocomplete inputId="ppf-address" value={address} onChange={setAddress}
              onPlaceSelect={(addr, c, ct) => { coordsFromAutocompleteRef.current = `${c[0].toFixed(6)},${c[1].toFixed(6)}`; setAddress(addr); setCoords(c); if (ct) setCity(ct); onCoordinatesChange?.(c); }}
              onClear={() => { setAddress(''); setCoords(null); }}
              placeholder="Search address…" aria-required="true" />
            {coords && address && (
              <div className="addr-confirm" role="status">
                <span className="addr-confirm-dot" aria-hidden="true" />
                {address.split(',').slice(0, 2).join(',')}
              </div>
            )}
          </div>
        </div>

        {/* 02 The unit */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">02</span>
            <span className="form-section-title">The unit</span>
          </div>
          <div className="fs">
            <label htmlFor="ppf-budget" className="fs-label">Monthly Rent (CAD) <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
            <div className="rent-input-row">
              <span className="rent-prefix" aria-hidden="true">$</span>
              <input id="ppf-budget" className="rent-input" type="number" value={budget}
                onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) <= 9999) setBudget(v); }}
                placeholder="2200" min="1" max="9999" aria-required="true" />
              <span className="rent-suffix" aria-hidden="true">/mo</span>
            </div>
          </div>
          <div className="fs">
            <span id="ppf-proptype-label" className="fs-label">Property Type <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <div className="proptype-grid" role="group" aria-labelledby="ppf-proptype-label">
              {PROP_TYPES.map(pt => (
                <button type="button" key={pt.label}
                  className={`proptype-tile${propType === pt.label ? ' on' : ''}`}
                  aria-pressed={propType === pt.label}
                  onClick={() => setPropType(propType === pt.label ? '' : pt.label)}>
                  <span className="proptype-tile-icon" aria-hidden="true">{pt.icon}</span>
                  <span>{pt.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="fs">
            <span id="ppf-bhk-label" className="fs-label">Bedrooms <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <div className="bhk-group" role="group" aria-labelledby="ppf-bhk-label">
              {BHK_OPTS.map(o => (
                <button type="button" key={String(o)} className={`bhk-tile${bhk === o ? ' on' : ''}`} aria-pressed={bhk === o} onClick={() => setBhk(o)}>{o}</button>
              ))}
            </div>
          </div>
          <div className="fs">
            <span id="ppf-baths-label" className="fs-label">Baths <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <div className="bhk-group" role="group" aria-labelledby="ppf-baths-label">
              {BATH_OPTS.map(o => (
                <button type="button" key={String(o)} className={`bhk-tile${baths === o ? ' on' : ''}`} aria-pressed={baths === o} onClick={() => setBaths(o)}>{o}</button>
              ))}
            </div>
          </div>
          <div className="fs">
            <span id="ppf-avail-label" className="fs-label">Availability <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></span>
            <Tgl opts={AVAIL} val={avail} onSet={setAvail} labelId="ppf-avail-label" />
          </div>
          <div className="fs">
            <span id="ppf-furnish-label" className="fs-label">Furnishing</span>
            <Tgl opts={FURNISH} val={furnishing} onSet={setFurnishing} labelId="ppf-furnish-label" />
          </div>
        </div>

        {/* 03 Extras */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">03</span>
            <span className="form-section-title">Extras &amp; neighbourhood</span>
          </div>
          <div className="fs"><span id="ppf-util-label" className="fs-label">Utilities included</span><Chips opts={UTILITIES} sel={utilities} setSel={setUtilities} labelId="ppf-util-label" /></div>
          <div className="fs"><span id="ppf-amen-label" className="fs-label">Amenities</span><Chips opts={AMENITIES} sel={amenities} setSel={setAmenities} labelId="ppf-amen-label" /></div>
          <div className="fs"><span id="ppf-pets-label" className="fs-label">Pets allowed</span><Tgl opts={PETS} val={pets} onSet={setPets} labelId="ppf-pets-label" /></div>
          <div className="fs"><span id="ppf-transit-label" className="fs-label">Transit access</span><Tgl opts={TRANSIT} val={transit} onSet={v => setTransit(transit === v ? '' : v)} labelId="ppf-transit-label" /></div>
          <div className="fs">
            <span id="ppf-school-label" className="fs-label">School rating (0–10)</span>
            <div className="dot-rating" role="group" aria-labelledby="ppf-school-label">
              {Array.from({ length: 11 }, (_, i) => (
                <button key={i} type="button"
                  className={`dot-seg${i <= schoolRating && schoolRating > 0 ? ' filled' : ''}`}
                  aria-pressed={schoolRating === i && i > 0}
                  aria-label={i === 0 ? 'Unknown' : `${i} out of 10`}
                  onClick={() => setSchoolRating(schoolRating === i ? 0 : i)} />
              ))}
            </div>
            <div className="dot-rating-labels" aria-live="polite">
              <span>0 · Unknown</span>
              <span>{schoolRating > 0 ? `${schoolRating}/10` : '—'}</span>
              <span>10 · Excellent</span>
            </div>
          </div>
        </div>

        {/* 04 Contact */}
        <div className="form-section">
          <div className="form-section-head" aria-hidden="true">
            <span className="form-section-num">04</span>
            <span className="form-section-title">Contact &amp; description</span>
          </div>
          <div className="fs">
            <label htmlFor="ppf-desc" className="fs-label">One-liner <span className="fs-label-opt">optional · 200 chars</span></label>
            <textarea id="ppf-desc" className="fi" rows={2} value={description}
              onChange={e => setDescription(e.target.value.slice(0, 200))} placeholder="Describe the unit briefly…"
              aria-describedby="ppf-desc-cc" />
            <span id="ppf-desc-cc" className="cc" aria-live="polite">{description.length}/200</span>
          </div>
          <div className="g2">
            <div className="fs">
              <label htmlFor="ppf-email" className="fs-label">Email <span aria-hidden="true" className="fs-label-req">*</span><span className="sr-only">(required)</span></label>
              <input id="ppf-email" className="fi" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" aria-required="true" />
            </div>
            <div className="fs">
              <label htmlFor="ppf-phone" className="fs-label">Phone <span className="fs-label-opt">optional</span></label>
              <input id="ppf-phone" className="fi" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 647…" />
            </div>
          </div>
        </div>

        <div className="consent-box">
          <label className="consent-label">
            <input
              type="checkbox"
              className="consent-check"
              checked={termsAccepted}
              onChange={e => {
                setTermsAccepted(e.target.checked);
                setConsentAt(e.target.checked ? new Date().toISOString() : null);
              }}
            />
            <span className="consent-text">
              I agree to the{' '}
              <button type="button" className="consent-link" onClick={() => onOpenTerms?.()}>Terms of Use</button>
              {' '}and{' '}
              <button type="button" className="consent-link" onClick={() => onOpenPrivacy?.()}>Privacy Policy</button>
              . I understand my email is used only to manage this listing and will be deleted after 30 days of inactivity.
            </span>
          </label>
        </div>
      </div>

      <div className="form-actions">
        {error && <div className="err-footer" role="alert">{error}</div>}
        <span className="form-progress" aria-live="polite">{sectionsComplete}/4 · {coords ? 'ready' : 'set location'}</span>
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
        <button type="button" className="btn-submit" onClick={handleSubmit} disabled={loading} aria-busy={loading}>
          {loading ? 'Posting…' : '🏢 Post Property'}
        </button>
      </div>
    </div>
  );
};
